<template>
  <layout-vendor-dashboard-inner
    :vendor-id="vendorId"
    :back-link="{ name: 'company-vendorId-dashboard', params: { vendorId } }">
    <div
      class="flex-column l:flex-row gap-l l:gap-s margin-2xl-bottom l:padding-max-inline"
      style="justify-content: space-around">
      <div class="flex-column flex-middle">
        <span class="text-max bold">{{
          translateNumber(profits, "currency", { currency: "EUR" })
        }}</span>
        <p class="text-center">{{ t("justopp.vendorStatistics.totalSales") }}</p>
      </div>
      <div class="flex-column flex-middle">
        <span class="text-max bold">{{ salesMade }}</span>
        <p class="text-center">{{ t("justopp.vendorStatistics.salesMade") }}</p>
      </div>
      <div class="flex-column flex-middle">
        <span class="text-max bold">{{ bidsReceived }}</span>
        <p class="text-center">{{ t("justopp.vendorStatistics.bidsReceived") }}</p>
      </div>
    </div>
    <div class="flex-between l:flex-row flex-column gap-2xl l:gap-s">
      <card
        class="flex-column deal-views"
        title="justopp.vendorStatistics.dealViews.title">
        <template #body>
          <div class="card__body">
            <products-view
              class="content"
              :vendor-id="vendorId"
              :override-list-configuration="listConfiguration" />
          </div>
        </template>
      </card>
      <card class="deal-views">
        <template #body>
          <div class="card__body">
            <div class="l:flex-center flex-column gap-l l:gap-max">
              <div class="flex-column flex-middle">
                <span class="text-2xl">{{ t(vendorProfile.name) }}</span>
              </div>
              <div class="flex-column flex-middle">
                <span class="text-2xl">{{
                  translateDate(vendorProfile.createdAt, "short")
                }}</span>
                <p class="text-center">{{ t("justopp.vendorStatistics.joinedAt") }}</p>
              </div>
            </div>
            <div class="info-container padding-max-top">
              <div class="flex-column flex-middle">
                <span class="text-2xl text-center">{{
                  highestSale
                    ? translateNumber(highestSale, "currency", { currency: "EUR" })
                    : "No sales yet"
                }}</span>
                <p class="">{{ t("justopp.vendorStatistics.highestSale") }}</p>
              </div>
              <div class="flex-column flex-middle">
                <span class="text-2xl text-center">{{
                  mostBidOnItem?.name || "No bids yet"
                }}</span>
                <p class="">{{ t("justopp.vendorStatistics.mostBidOn") }}</p>
              </div>
              <div class="flex-column flex-middle">
                <span class="text-2xl text-center">{{
                  latestSale
                    ? translateDate(latestSale.createdAt, "dateWithTime")
                    : "No sales yet"
                }}</span>
                <p class="">{{ t("justopp.vendorStatistics.latestSale") }}</p>
              </div>
              <div class="flex-column flex-middle">
                <span class="text-2xl text-center">{{
                  latestBid
                    ? translateDate(latestBid.createdAt, "dateWithTime")
                    : "No sales yet"
                }}</span>
                <p class="">{{ t("justopp.vendorStatistics.latestBid") }}</p>
              </div>
              <div class="flex-column flex-middle">
                <span class="text-2xl text-center">{{
                  translateNumber(totalIntroViews)
                }}</span>
                <p class="">{{ t("justopp.vendorStatistics.totalIntroViews") }}</p>
              </div>
              <div class="flex-column flex-middle">
                <span class="text-2xl text-center">{{
                  translateNumber(totalViews)
                }}</span>
                <p class="">{{ t("justopp.vendorStatistics.totalViews") }}</p>
              </div>
            </div>
          </div>
        </template>
      </card>
    </div>
  </layout-vendor-dashboard-inner>
</template>

<script>
import asyncOperations from "@/client/extensions/composition/asyncOperations.js";
import ProductsView from "@/client/applications/justopp/views/Products";

export default {
  components: {
    ProductsView,
  },
  layout: {
    component: "noContainer",
    params: {
      title: "justopp.vendorStatistics.pageTitle",
      subTitle: "justopp.vendorStatistics.pageSubtitle",
      containerClass: "margin-remove-vertical",
    },
  },
  middleware: {
    requireVendorOrAdmin: { vendorIdFromParam: "vendorId" },
  },
  props: {
    // vendor ID
    vendorId: {
      type: String,
      default: 0,
    },
  },
  setup() {
    const { asyncOps, asyncOpsReady, asyncStatus } = asyncOperations();
    return { asyncOps, asyncOpsReady, asyncStatus };
  },
  meta: "justopp.vendorDashboard.browserTitle",
  data() {
    return {
      orders: false,
      bids: false,
      products: false,
      listConfiguration: {
        showIdField: false,
        enableMassActions: false,
        fields: {
          name: {
            itemKey: "name",
            label: "justopp.vendorDeals.fields.name",
            orderable: false,
          },
          expandedViewCount: {
            itemKey: "expandedViewCount",
            label: "justopp.vendorDeals.fields.expandedViewCount",
            filterable: false, // boolean, or object
            orderable: true,
          },
          introViewCount: {
            itemKey: "introViewCount",
            label: "justopp.vendorDeals.fields.introViewCount",
            filterable: false, // boolean, or object
            orderable: true,
          },
          clickRate: {
            label: "justopp.vendorDeals.fields.clickRate",
            itemKey: "clickRate",
            orderable: false,
            filterable: false,
          },
        },
        actions: false,
        paginationConfig: {
          start: 0,
          limit: 5,
          show: true,
        },
        constantFilters: {
          VendorId: this.vendorId,
        },
        initialOrdering: {
          key: "expandedViewCount",
          direction: "desc",
        },
      },
      asyncData: {
        orders: {
          target: "ecommerce/order",
          data: {
            filters: { VendorId: this.vendorId },
          },
          options: {
            dataMutator: (data) => {
              return data.items ? data.items : [];
            },
          },
        },
        bids: {
          target: "justopp/bid",
          data: {
            filters: { VendorId: this.vendorId },
          },
          options: {
            dataMutator: (data) => {
              return data.items ? data.items : [];
            },
          },
        },
        products: {
          target: "ecommerce/product",
          data: {
            filters: { VendorId: this.vendorId },
          },
          options: {
            dataMutator: (data) => {
              return data.items ? data.items : [];
            },
          },
        },
      },
    };
  },
  computed: {
    vendorProfile() {
      return this.$store.getters["user/profile"]?.Vendor;
    },
    latestSale() {
      return !this.orders
        ? undefined
        : [...this.orders].sort(
            (orderA, orderB) => orderB.createdAt - orderA.createdAt
          )[0];
    },
    latestBid() {
      return !this.bids
        ? undefined
        : [...this.bids].sort((bidA, bidB) => bidB.createdAt - bidA.createdAt)[0];
    },
    salesMade() {
      return !this.orders ? 0 : this.orders.reduce((sum) => sum + 1, 0);
    },
    bidsReceived() {
      return !this.bids ? 0 : this.bids.reduce((sum) => sum + 1, 0);
    },
    profits() {
      return !this.orders
        ? 0
        : this.orders.reduce((sum, order) => sum + order.Quote.grandTotal, 0);
    },
    highestSale() {
      return !this.orders
        ? 0
        : this.orders.reduce(
            (highest, order) => Math.max(highest, order.Quote.grandTotal),
            0
          );
    },
    mostBidOnItem() {
      if (!this.bids) {
        return undefined;
      }

      const items = this.bids.map((bid) => bid.AuctionProductType.Product);
      return [...items].sort(
        (itemA, itemB) =>
          items.filter((item) => item.name === itemA.name).length -
          items.filter((item) => item.name === itemB.name).length
      )[0];
    },
    totalViews() {
      return !this.products
        ? 0
        : this.products.reduce((sum, product) => sum + product.expandedViewCount, 0);
    },
    totalIntroViews() {
      return !this.products
        ? 0
        : this.products.reduce((sum, product) => sum + product.introViewCount, 0);
    },
  },
  watch: {},
  methods: {},
};
</script>

<style lang="scss" scoped>
.deal-views {
  flex: 0 0 calc(50% - var(--margin-2xl) / 2);

  :deep(.overflow-x-scroll) {
    overflow-x: auto !important;
  }

  :deep(td span) {
    min-width: 0px !important;
  }
}

.info-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: var(--margin-l);
  row-gap: var(--margin-2xl);

  @media (max-width: 1024px) {
    display: flex;
    flex-direction: column;
  }
}

:deep(.pagination ~ div) {
  display: none;
}
</style>
